/**
 * GatewayChrome — the chrome Gateway > VLANs and Gateway > DHCP share.
 *
 * Both screens edit ONE document (a uCentral gateway's whole configuration) through ONE write
 * path (POST /gateway/plan then /gateway/apply). So the parts that carry the safety rules —
 * the gateway picker, the "cannot edit yet" gate, the locked WAN card and, above all, the
 * propose-then-confirm review dialog — live here once. Two copies of a confirm dialog on a
 * device class where one wrong push strands the box is two chances for them to drift.
 *
 * Each screen supplies only what is genuinely its own: the row-level `delta` it renders
 * ahead of the raw diff, and the sentence describing what a read-back verdict means for its
 * kind of change.
 */
import { useEffect, useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  usePlanGatewayChange,
  useApplyGatewayChange,
  useProposeAction,
  useConfirmProposal,
  useRejectProposal,
} from "@/lib/api/hooks";
import type {
  AiProposal,
  GatewayApplyResult,
  GatewayChangeSpec,
  GatewayIntent,
  GatewayPlan,
} from "@/types/domain";
import { ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { ProposalCard } from "@/components/ai/ProposalCard";
import { cn } from "@/lib/cn";

/** Shared input chrome (matches SwitchVlans/RadiusServers/SSIDs). */
export const INPUT =
  "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";

/** A pending change: what the operator asked for, before it has been planned. */
export interface PendingGatewayChange {
  intent: GatewayIntent;
  spec: GatewayChangeSpec;
  /** Verb used in the review panel's button, e.g. "Add VLAN". */
  label: string;
}

export function GatewayPicker({
  serial,
  gateways,
  loading,
  onChange,
}: {
  serial: string | null;
  gateways: { serial: string; name: string }[];
  loading: boolean;
  onChange: (serial: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-ink-muted">Gateway</span>
      <select
        className={`${INPUT} min-h-touch`}
        value={serial ?? ""}
        disabled={loading || gateways.length === 0}
        onChange={(e) => onChange(e.target.value)}
      >
        {gateways.length === 0 && <option value="">{loading ? "Loading…" : "None"}</option>}
        {gateways.map((g) => (
          <option key={g.serial} value={g.serial}>
            {g.name || g.serial}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Why the editor is unavailable — the server's own sentence, not a generic "error". */
export function NotEditableBanner({ title, reason }: { title: string; reason: string }) {
  return (
    <div role="status" className="md-card border-warn/40 bg-warn-soft p-4 text-sm">
      <div className="font-medium text-ink">{title}</div>
      <div className="mt-1 text-ink-muted">{reason}</div>
    </div>
  );
}

/**
 * The WAN, locked. The danger is the point of the card — an operator must not have to hover
 * anything to learn that this is the interface that can strand the box.
 */
export function LockedWanCard({
  name,
  summary,
  ports,
  reason,
  children,
}: {
  name: string | null;
  summary: string;
  ports: string[];
  reason: string | null | undefined;
  children?: ReactNode;
}) {
  return (
    <section className="md-card border-critical/40 bg-critical-soft p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-critical px-1.5 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-invert">
          WAN
        </span>
        <span className="font-medium text-ink">{name ?? "—"}</span>
        <span className="font-mono text-xs text-ink-muted">{summary}</span>
        {ports.map((p) => (
          <span key={p} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem]">
            {p}
          </span>
        ))}
        <span className="ml-auto rounded border border-critical/50 px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-critical">
          Locked
        </span>
      </div>
      {children}
      {reason && <p className="mt-2 max-w-3xl text-xs text-critical">{reason}</p>}
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Review + confirm. The ONLY place either screen writes to the device.
 * ------------------------------------------------------------------ */

export function GatewayReviewDialog({
  serial,
  change,
  onClose,
  onApplied,
  onPushed,
  delta,
  outcomeText,
}: {
  serial: string;
  change: PendingGatewayChange;
  onClose: () => void;
  /** The operator pressed Done after a successful push. */
  onApplied: () => void;
  /** Fired once, the moment /apply succeeds — for controller-side follow-ups (e.g. a DHCP
   *  label) that must only be written when the device change was actually accepted. */
  onPushed?: (result: GatewayApplyResult) => void;
  /** The screen's row-level before/after, rendered ahead of the raw diff. */
  delta: (plan: GatewayPlan) => ReactNode;
  /** What the read-back verdict means for this screen's kind of change. */
  outcomeText: (result: { changed: boolean; verdict?: string }) => string;
}) {
  const plan = usePlanGatewayChange(serial);
  const apply = useApplyGatewayChange(serial);
  const [result, setResult] = useState<{ changed: boolean; verdict?: string } | null>(null);

  // --- "Propose with assistant": file the SAME {intent, spec} as a reviewable AiProposal instead of
  //     pushing it directly. Purely additive — the manual plan/apply path above is untouched. The
  //     operator can turn any reviewed change into a proposal that a human (maybe another operator)
  //     confirms later; the propose/confirm floors equal the manual gateway-write floor. ---
  const propose = useProposeAction();
  const confirm = useConfirmProposal();
  const reject = useRejectProposal();
  const [showPropose, setShowPropose] = useState(false);
  const [rationale, setRationale] = useState("");
  const [proposed, setProposed] = useState<AiProposal | null>(null);

  // Plan once, as soon as the dialog opens. Read-only on the server: no push, no DB write.
  const { mutate: runPlan } = plan;
  useEffect(() => {
    runPlan({ intent: change.intent, spec: change.spec });
  }, [runPlan, change.intent, change.spec]);

  const planned = plan.data;

  // Send the reviewed change to the proposal queue — the identical intent/spec, to /propose.
  const sendProposal = () =>
    propose.mutate(
      {
        kind: "gateway.config",
        target: serial,
        spec: { intent: change.intent, spec: change.spec },
        rationale: rationale.trim() || undefined,
      },
      { onSuccess: (p) => setProposed(p) },
    );

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            Review change to {serial}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Nothing has been sent to the gateway yet. This is what would be pushed.
          </Dialog.Description>

          {plan.isPending && (
            <div className="mt-4 text-sm text-ink-muted">Working out what would change…</div>
          )}
          {plan.isError && (
            <div className="mt-4">
              <ErrorBanner error={plan.error} />
            </div>
          )}

          {/* REVIEW: the manual push path (untouched) + the "Propose with assistant" alternative. */}
          {planned && !result && !proposed && (
            <div className="mt-4 flex flex-col gap-4">
              {!planned.changed ? (
                <EmptyState
                  title="Nothing would change"
                  hint="The gateway already holds this configuration, so there is nothing to push."
                />
              ) : (
                <>
                  {delta(planned)}
                  <PlanWarnings warnings={planned.warnings} />
                  <RawDiff plan={planned} />
                </>
              )}

              {apply.isError && <ErrorBanner error={apply.error} />}
              {propose.isError && <ErrorBanner error={propose.error} />}

              {/* Optional rationale note that rides along with the proposal (stored verbatim). */}
              {showPropose && planned.changed && (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Note for the reviewer (optional)</span>
                  <textarea
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    rows={2}
                    maxLength={4000}
                    placeholder="Why this change — the reviewer sees this with the diff."
                    aria-label="Proposal rationale"
                    className={`${INPUT} min-h-touch w-full resize-y`}
                  />
                </label>
              )}

              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={apply.isPending || propose.isPending}>
                  Cancel
                </Button>
                {/* Turn the reviewed change into a proposal instead of applying it directly. */}
                {planned.changed && (
                  <RoleGate
                    min="org_admin"
                    mode="disable"
                    reason="Proposing a gateway change for review requires a customer admin."
                  >
                    <Button
                      variant="secondary"
                      disabled={propose.isPending || apply.isPending}
                      onClick={() => (showPropose ? sendProposal() : setShowPropose(true))}
                    >
                      {propose.isPending
                        ? "Filing…"
                        : showPropose
                          ? "Send to review"
                          : "Propose with assistant"}
                    </Button>
                  </RoleGate>
                )}
                <RoleGate
                  min="org_admin"
                  mode="disable"
                  reason="Applying a gateway configuration requires a customer admin."
                >
                  <Button
                    variant="danger"
                    disabled={!planned.changed || apply.isPending || propose.isPending}
                    onClick={() =>
                      apply.mutate(
                        {
                          intent: change.intent,
                          spec: change.spec,
                          plan_token: planned.plan_token,
                        },
                        {
                          onSuccess: (r) => {
                            onPushed?.(r);
                            setResult({ changed: r.changed, verdict: r.verification?.verdict });
                          },
                        },
                      )
                    }
                  >
                    {apply.isPending ? "Pushing…" : `${change.label} — push to gateway`}
                  </Button>
                </RoleGate>
              </div>
            </div>
          )}

          {/* PROPOSED: the filed AiProposal, reviewed + confirmed/rejected inline via the shared card.
              Confirming pushes through the SAME manual write path; a drift refuses it (409) and the
              card flips to its expired state from confirm.error's body. */}
          {planned && !result && proposed && (
            <div className="mt-4 flex flex-col gap-3">
              <ProposalCard
                proposal={proposed}
                busy={confirm.isPending || reject.isPending}
                confirmError={confirm.error}
                onConfirm={() =>
                  confirm.mutate(proposed.id, { onSuccess: (res) => setProposed(res.proposal) })
                }
                onReject={() =>
                  reject.mutate(proposed.id, { onSuccess: (p) => setProposed(p) })
                }
              />
              <div className="flex justify-end">
                <Button variant="primary" onClick={onApplied}>
                  Close
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="md-card border-healthy/40 bg-healthy-soft p-4 text-sm">
                <div className="font-medium text-ink">
                  {result.changed ? "Pushed to the gateway" : "Nothing to push — already applied"}
                </div>
                <div className="mt-1 text-ink-muted">{outcomeText(result)}</div>
              </div>
              <div className="flex justify-end">
                <Button variant="primary" onClick={onApplied}>
                  Done
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Server warnings. Never blocks; always rendered, in the server's words. */
export function PlanWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <ul className="md-card border-warn/40 bg-warn-soft p-3 text-sm text-ink">
      {warnings.map((w) => (
        <li key={w} className="flex gap-2 py-0.5">
          <span className="text-warn">!</span>
          <span>{w}</span>
        </li>
      ))}
    </ul>
  );
}

/** The raw redacted config diff, collapsed. The row delta is the readable view; this is the
 *  receipt for an operator who wants to see the document that will be pushed. */
export function RawDiff({ plan }: { plan: GatewayPlan }) {
  return (
    <details className="md-card overflow-hidden">
      <summary className="cursor-pointer px-4 py-2 text-sm text-ink-muted">
        Configuration diff ({plan.diff.length} section{plan.diff.length === 1 ? "" : "s"})
      </summary>
      <ul className="max-h-64 divide-y divide-line overflow-y-auto">
        {plan.diff.map((d, i) => (
          <li key={`${d.path}-${i}`} className="px-4 py-2 font-mono text-[0.6875rem]">
            <div className="text-ink-muted">
              <span
                className={cn(
                  "mr-2 font-bold",
                  d.op === "added"
                    ? "text-healthy"
                    : d.op === "removed"
                      ? "text-critical"
                      : "text-warn",
                )}
              >
                {d.op === "added" ? "+" : d.op === "removed" ? "−" : "~"}
              </span>
              {d.path}
            </div>
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-ink-subtle">
              {JSON.stringify(d.after ?? d.before, null, 1)}
            </pre>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Added / removed / changed chip used by both screens' row deltas. */
export function DeltaChip({ kind }: { kind: "added" | "removed" | "changed" }) {
  return (
    <span
      className={cn(
        "ml-1 rounded px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide",
        kind === "added"
          ? "bg-healthy/10 text-healthy"
          : kind === "removed"
            ? "bg-critical/10 text-critical"
            : "bg-warn/10 text-warn",
      )}
    >
      {kind}
    </span>
  );
}
